"use client";

import { useState } from "react";
import Image from "next/image";
import { FacebookIcon, LinkedinIcon, TwitterIcon } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ShareAndAuthorInfoProps {
  authorName?: string;
  authorImageUrl?: string;
  editorialInfo?: string;
}

export default function ShareAndAuthorInfo({
  authorName = "StudyStash Editorial Team",
  authorImageUrl = "/team/jonathan.png",
  editorialInfo = `StudyStash builds evidence-informed learning tools for higher education.
Our pilots focus on measurable outcomes for students and teaching teams.`,
}: ShareAndAuthorInfoProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const normalisedEditorialInfo = editorialInfo.trim();
  const canExpand = normalisedEditorialInfo.length > 240;
  const displayedInfo = canExpand && !isExpanded
    ? `${normalisedEditorialInfo.slice(0, 240).trimEnd()}…`
    : normalisedEditorialInfo;

  const handleShare = (platform: "linkedin" | "twitter" | "facebook") => {
    const url = window.location.href;
    const title = document.title;
    
    const shareUrls = {
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    };

    window.open(shareUrls[platform], "_blank", "width=600,height=400");
  };

  return (
    <div className="mt-10">
      <hr className="mb-10 border-ss-light-blue" />
      {/* Share Row */}
      <div className="mb-8 flex items-center gap-4">
        <span className="text-sm font-semibold uppercase tracking-[0.08em] text-gray-600">Share this article</span>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 p-0"
            onClick={() => handleShare("linkedin")}
            aria-label="Share on LinkedIn"
          >
            <LinkedinIcon className="h-5 w-5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 p-0"
            onClick={() => handleShare("twitter")}
            aria-label="Share on Twitter"
          >
            <TwitterIcon className="h-5 w-5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 p-0"
            onClick={() => handleShare("facebook")}
            aria-label="Share on Facebook"
          >
            <FacebookIcon className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Author/Editorial Info Block */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex items-start gap-5">
          <div className="shrink-0">
            <Image
              src={authorImageUrl}
              alt="Author"
              width={72}
              height={72}
              className="aspect-square rounded-full object-cover"
            />
          </div>
          <div className="grow">
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">About the author</p>
            <p className="mt-1 text-base font-semibold text-gray-900 sm:text-lg">{authorName}</p>
            <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-gray-700 sm:text-base">
              {displayedInfo}
            </p>
            {canExpand ? (
              <button
                type="button"
                onClick={() => setIsExpanded((prev) => !prev)}
                className="mt-3 text-sm font-medium text-ss-blue transition-colors hover:text-ss-light-blue"
              >
                {isExpanded ? "Show less" : "Read more"}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
